import {
  HankenGrotesk_400Regular,
  HankenGrotesk_500Medium,
  HankenGrotesk_600SemiBold,
  HankenGrotesk_700Bold,
  HankenGrotesk_800ExtraBold,
  useFonts,
} from "@expo-google-fonts/hanken-grotesk";
import {
  Newsreader_400Regular,
  Newsreader_500Medium,
} from "@expo-google-fonts/newsreader";
import {
  JetBrainsMono_400Regular,
  JetBrainsMono_500Medium,
} from "@expo-google-fonts/jetbrains-mono";
import {
  installConsoleCapture,
  ObservaErrorBoundary,
  ObservaProvider,
  useObserva,
  useObservaGlobalHandlers,
} from "@morganisbatman/observa-client-react";
import { Stack } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";

import { colors, fontFamily } from "@passfirst/ui";

import { API_URL } from "../src/env";
import { NetworkBanner } from "../src/network-banner";
import { configureNotificationHandler } from "../src/notifications/schedule";
import { registerObservaClient } from "../src/observa-capture";
import { SplashOverlay } from "../src/splash-overlay";
import { ThemeProvider } from "../src/theme";

// Configure how study reminders present when the app is foregrounded.
// Module scope (per Expo guidance) so it's set before any notification can
// arrive. No-op on web/SSR; sets the real handler on native.
configureNotificationHandler();

// Observability must never take down the product UI. A failed telemetry
// emit (the observa ingest endpoint unreachable/misconfigured — e.g. the
// 2026-06 stray-`/trpc` outage) surfaces as a tRPC "Unable to transform
// response from server" / BackendRequestError. If one of those ever reaches
// this boundary we auto-recover instead of white-screening the whole app —
// the telemetry is lost but the user keeps going. Genuine app render errors
// still fall through to the recoverable fallback below.
function isTelemetryError(err: Error): boolean {
  const message = err?.message ?? "";
  const name = err?.name ?? "";
  const stack = err?.stack ?? "";
  return (
    name === "BackendRequestError" ||
    message.includes("Unable to transform response from server") ||
    stack.includes("/_observability/")
  );
}

// Bound the auto-recovery so a genuinely persistent telemetry-in-render
// fault can't spin error→reset→error forever — after a burst we fall through
// to the visible fallback rather than pegging the render loop.
const recentTelemetryRecoveries: number[] = [];
function shouldAutoRecoverTelemetry(): boolean {
  const now = Date.now();
  while (
    recentTelemetryRecoveries.length > 0 &&
    now - (recentTelemetryRecoveries[0] ?? 0) > 10_000
  ) {
    recentTelemetryRecoveries.shift();
  }
  if (recentTelemetryRecoveries.length >= 5) return false;
  recentTelemetryRecoveries.push(now);
  return true;
}

// Resets the boundary on mount so children re-render. Telemetry errors are
// async/transient, so the children render cleanly on the retry.
function TelemetryAutoRecover({ reset }: { reset: () => void }): null {
  useEffect(() => {
    reset();
  }, [reset]);
  return null;
}

// Render-prop fallback for ObservaErrorBoundary. observa captures the
// error + componentStack before this renders; we just show the user
// something they can recover from.
function renderErrorFallback(err: Error, reset: () => void): JSX.Element {
  if (isTelemetryError(err) && shouldAutoRecoverTelemetry()) {
    return <TelemetryAutoRecover reset={reset} />;
  }
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.background,
        padding: 24,
        gap: 16,
      }}
    >
      <Text
        style={{
          fontSize: 18,
          fontFamily: fontFamily.semiBold,
          color: colors.textPrimary,
        }}
      >
        Something went wrong.
      </Text>
      <Text
        style={{ fontSize: 14, color: colors.textMuted, textAlign: "center" }}
      >
        We&apos;ve logged the error. Try again, or reload the app.
      </Text>
      <Pressable
        onPress={reset}
        accessibilityRole="button"
        accessibilityLabel="Try again"
        style={{
          backgroundColor: colors.brand,
          paddingHorizontal: 20,
          paddingVertical: 12,
          borderRadius: 8,
        }}
      >
        <Text style={{ color: colors.actionText, fontFamily: fontFamily.semiBold }}>
          Try again
        </Text>
      </Pressable>
    </View>
  );
}

const OBSERVA_ENDPOINT =
  process.env.EXPO_PUBLIC_OBSERVA_API_URL ?? `${API_URL}/_observability/trpc`;

// The animated brand splash plays once per JS session. expo-router can
// remount the root layout on some navigation/deep-link re-entries; without
// this guard the full ~3.5s timeline would replay and re-block routing each
// time. The flag lives at module scope so it survives a layout remount but
// resets on a genuine cold start (fresh JS context).
let splashPlayed = false;

// Wires the in-tree observa client into the global handlers + console
// capture. Both need the client instance, which only exists inside the
// Provider — so this lives as a child component.
function ObservaWiring(): null {
  const client = useObserva();
  useObservaGlobalHandlers({
    captureSessions: true,
    capturePageViews: true,
  });
  useEffect(() => installConsoleCapture(client), [client]);
  // Expose the client to module-scope code (tRPC + auth fetch wrappers)
  // so backend transport failures surface as observa.error events.
  useEffect(() => registerObservaClient(client), [client]);
  return null;
}

export default function RootLayout(): JSX.Element {
  // Hanken Grotesk (UI + display) + Newsreader (serif, question stems
  // only) + JetBrains Mono (eyebrows) per the 2026 brand refresh — see
  // docs/design_refresh/README.md. Loading is async on native; gate the
  // tree behind it so screens don't flash a fallback font and reflow on
  // first render.
  const [fontsLoaded, fontError] = useFonts({
    HankenGrotesk_400Regular,
    HankenGrotesk_500Medium,
    HankenGrotesk_600SemiBold,
    HankenGrotesk_700Bold,
    HankenGrotesk_800ExtraBold,
    Newsreader_400Regular,
    Newsreader_500Medium,
    JetBrainsMono_400Regular,
    JetBrainsMono_500Medium,
  });

  // Font loading must never permanently white-screen the app. If the font
  // CDN is slow/unreachable (or a glyph file fails outright), fall back to
  // system fonts and render the tree rather than blocking on the spinner
  // forever. We render once fonts load, once `useFonts` reports an error,
  // or after a hard timeout — whichever comes first.
  const [fontTimedOut, setFontTimedOut] = useState(false);
  useEffect(() => {
    if (fontsLoaded || fontError) return;
    const t = setTimeout(() => setFontTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, [fontsLoaded, fontError]);
  const fontsReady = fontsLoaded || !!fontError || fontTimedOut;

  // Animated brand splash, shown once per JS session over the app tree.
  // If it already played (a layout remount within the same session), start
  // done so we don't replay the timeline and re-block routing.
  const [splashDone, setSplashDone] = useState(splashPlayed);

  if (!fontsReady) {
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background,
        }}
      >
        <ActivityIndicator color={colors.accentSoftText} />
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <ThemeProvider>
      <ObservaProvider
        config={{
          appId: "passfirstapp",
          appVersion: process.env.EXPO_PUBLIC_APP_VERSION ?? "dev",
          environment: __DEV__ ? "development" : "production",
          endpoint: OBSERVA_ENDPOINT,
          key: process.env.EXPO_PUBLIC_OBSERVA_PUBLIC_KEY,
        }}
      >
        <ObservaWiring />
        <ObservaErrorBoundary fallback={renderErrorFallback}>
          <Stack screenOptions={{ headerShown: false }} />
          <NetworkBanner />
        </ObservaErrorBoundary>
      </ObservaProvider>
      {!splashDone ? (
        <SplashOverlay
          onDone={() => {
            splashPlayed = true;
            setSplashDone(true);
          }}
        />
      ) : null}
      </ThemeProvider>
    </SafeAreaProvider>
  );
}
